import seedConfig from "./demo-seed.json";
import type {
  DatabaseInstance,
  DemoData,
  Deployment,
  Environment,
  Health,
  Incident,
  Migration,
  PipelineStep,
  RunStatus,
  Scenario,
  StepStatus,
} from "../types";

const PIPELINE_NAMES = [
  "取出程式碼",
  "安裝依賴",
  "程式碼檢查",
  "型別檢查",
  "自動化測試",
  "建置",
  "安全稽核",
  "部署",
  "冒煙測試",
] as const;

const MIGRATION_STEPS = [
  "驗證腳本",
  "相容性檢查",
  "備份檢查",
  "模擬執行",
  "套用變更",
  "結果驗證",
] as const;

interface RepositoryOptions {
  seed?: number;
  scenario?: Scenario;
}

export class DemoRepositoryError extends Error {
  constructor() {
    super("錯誤情境中的示範資料來源刻意設定為無法使用。");
    this.name = "DemoRepositoryError";
  }
}

function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let next = value;
    next = Math.imul(next ^ (next >>> 15), next | 1);
    next ^= next + Math.imul(next ^ (next >>> 7), next | 61);
    return ((next ^ (next >>> 14)) >>> 0) / 4294967296;
  };
}

function trend(
  random: () => number,
  baseline: number,
  spread: number,
): DatabaseInstance["trend"] {
  return Array.from({ length: 24 }, (_, index) => ({
    label: `${String(index).padStart(2, "0")}:00`,
    value: Math.max(
      0,
      Math.round((baseline + (random() - 0.5) * spread) * 10) / 10,
    ),
  }));
}

function healthFor(scenario: Scenario, index: number): Health {
  if (scenario === "healthy") return "Healthy";
  if (scenario === "incident")
    return index < 2 ? "Critical" : index < 4 ? "Warning" : "Healthy";
  if (scenario === "degraded") return index < 4 ? "Warning" : "Healthy";
  return index === 1
    ? "Critical"
    : index === 3 || index === 5
      ? "Warning"
      : "Healthy";
}

function buildInstances(
  random: () => number,
  scenario: Scenario,
): DatabaseInstance[] {
  const specs = [
    [
      "pg-orders-prod",
      "PostgreSQL",
      "16.4",
      "Production",
      "ap-northeast-1",
      "主要節點",
    ],
    [
      "pg-orders-replica",
      "PostgreSQL",
      "16.4",
      "Production",
      "ap-northeast-1",
      "複本節點",
    ],
    [
      "mysql-billing-prod",
      "MySQL",
      "8.4.2",
      "Production",
      "ap-east-1",
      "主要節點",
    ],
    [
      "redis-session-prod",
      "Redis",
      "7.4.1",
      "Production",
      "ap-northeast-1",
      "主要節點",
    ],
    [
      "pg-catalog-stg",
      "PostgreSQL",
      "17.1",
      "Staging",
      "ap-northeast-1",
      "主要節點",
    ],
    [
      "mysql-reporting-stg",
      "MySQL",
      "8.4.2",
      "Staging",
      "ap-east-1",
      "複本節點",
    ],
    ["redis-feature-dev", "Redis", "7.4.1", "Dev", "本機", "獨立節點"],
    ["pg-sandbox-dev", "PostgreSQL", "17.1", "Dev", "本機", "獨立節點"],
  ] as const;

  return specs.map((spec, index) => {
    const health = healthFor(scenario, index);
    const critical = health === "Critical";
    const warning = health === "Warning";
    const cpu = Math.round(
      critical
        ? 91 + random() * 6
        : warning
          ? 67 + random() * 15
          : 24 + random() * 34,
    );
    return {
      id: `db-${index + 1}`,
      name: spec[0],
      engine: spec[1],
      version: spec[2],
      environment: spec[3],
      region: spec[4],
      endpoint: `${spec[0]}.demo.internal`,
      role: spec[5],
      health,
      cpu,
      memory: Math.round(critical ? 88 + random() * 8 : 42 + random() * 38),
      storage: Math.round(
        warning || critical ? 78 + random() * 17 : 31 + random() * 38,
      ),
      connections: Math.round(80 + random() * 780),
      connectionLimit: 1000,
      slowQueries: critical ? 42 : warning ? 14 : Math.round(random() * 5),
      replicationLagMs:
        spec[5] === "複本節點"
          ? critical
            ? 18_400
            : Math.round(random() * 800)
          : 0,
      uptimeDays: Math.round(25 + random() * 420),
      owner: ["資料平台團隊", "商務 DBA 團隊", "SRE 核心團隊"][index % 3],
      trend: trend(random, cpu, critical ? 22 : 12),
    };
  });
}

function buildIncidents(
  instances: DatabaseInstance[],
  scenario: Scenario,
): Incident[] {
  if (scenario === "healthy") return [];
  const definitions = [
    ["複寫延遲超過 SLO", "db-2", "Critical", "replication_lag = 18.4s"],
    [
      "Redis 記憶體碎片率持續上升",
      "db-4",
      "Warning",
      "mem_fragmentation_ratio = 1.92",
    ],
    [
      "預估儲存空間將在 7 天內不足",
      "db-6",
      "Warning",
      "disk_free_forecast = 5.8d",
    ],
    ["偵測到連線數接近飽和", "db-1", "Healthy", "connections = 842 / 1000"],
  ] as const;
  return definitions.map((item, index) => {
    const instance =
      instances.find((candidate) => candidate.id === item[1]) ?? instances[0];
    return {
      id: `inc-${2401 + index}`,
      title: item[0],
      instanceId: item[1],
      environment: instance?.environment ?? "Production",
      severity: item[2],
      status: index === 3 ? "Resolved" : index === 2 ? "Acknowledged" : "Open",
      startedAt: `2026-07-17T0${index + 1}:2${index}:00.000Z`,
      owner: index === 0 ? "值班 DBA" : "SRE 核心團隊",
      signal: item[3],
    };
  });
}

function stepStatus(
  runStatus: RunStatus,
  index: number,
  failedAt: number,
): StepStatus {
  if (runStatus === "Queued") return "Pending";
  if (runStatus === "Running")
    return index < 4 ? "Succeeded" : index === 4 ? "Running" : "Pending";
  if (runStatus === "Failed")
    return index < failedAt
      ? "Succeeded"
      : index === failedAt
        ? "Failed"
        : "Skipped";
  if (runStatus === "Cancelled") return index < 2 ? "Succeeded" : "Skipped";
  return "Succeeded";
}

function buildSteps(
  names: readonly string[],
  status: RunStatus,
  failedAt = 6,
): PipelineStep[] {
  return names.map((name, index) => ({
    name,
    status: stepStatus(status, index, failedAt),
    durationSeconds: index * 7 + 8,
  }));
}

function buildDeployments(scenario: Scenario): Deployment[] {
  const statuses: RunStatus[] =
    scenario === "healthy"
      ? ["Succeeded", "Succeeded", "Succeeded", "Succeeded"]
      : scenario === "deployment-failure"
        ? [
            "Failed",
            "Running",
            "Succeeded",
            "Rolled Back",
            "Queued",
            "Cancelled",
          ]
        : [
            "Running",
            "Succeeded",
            "Failed",
            "Rolled Back",
            "Queued",
            "Cancelled",
          ];
  return statuses.map((status, index) => {
    const environment =
      index % 3 === 0 ? "Production" : index % 3 === 1 ? "Staging" : "Preview";
    const id = `dep-${801 + index}`;
    return {
      id,
      commit:
        ["a91fe42", "3e8bc19", "f41d902", "b73a21c", "18ce770", "d04ca11"][
          index
        ] ?? "0000000",
      message:
        [
          "feat: 新增線上索引保護機制",
          "fix: 限制複本重試時間範圍",
          "chore: 更新基礎映像檔",
          "rollback: 回復查詢規劃器 v4",
          "feat: 新增遷移模擬執行報告",
          "test: 擴充冒煙測試涵蓋範圍",
        ][index] ?? "示範部署",
      branch: environment === "Preview" ? `feature/demo-${index}` : "main",
      environment,
      version: `v2.8.${12 - index}`,
      status,
      durationSeconds: status === "Queued" ? 0 : 178 + index * 31,
      url: `https://${environment.toLowerCase()}-${id}.pages.dev`,
      createdAt: `2026-07-17T${String(12 - index).padStart(2, "0")}:10:00.000Z`,
      actor: index % 2 ? "github-actions[bot]" : "wenlin",
      steps: buildSteps(PIPELINE_NAMES, status),
    };
  });
}

function buildMigrations(): Migration[] {
  const statuses: RunStatus[] = [
    "Succeeded",
    "Running",
    "Failed",
    "Rolled Back",
  ];
  return statuses.map((status, index) => ({
    id: `mig-${410 + index}`,
    name:
      [
        "20260717_add_order_lookup_index.sql",
        "20260717_backfill_customer_segment.sql",
        "20260716_alter_invoice_precision.sql",
        "20260715_drop_legacy_session_table.sql",
      ][index] ?? "migration.sql",
    database:
      [
        "pg-orders-prod",
        "pg-catalog-stg",
        "mysql-billing-prod",
        "redis-session-prod",
      ][index] ?? "demo",
    commit: ["9ac91d2", "ac10e7b", "f41d902", "2bc8a11"][index] ?? "0000000",
    environment: index === 1 ? "Staging" : "Production",
    status,
    durationSeconds: 52 + index * 29,
    rollbackAvailable: index !== 1,
    createdAt: `2026-07-${17 - index}T09:30:00.000Z`,
    steps: buildSteps(MIGRATION_STEPS, status, 3),
  }));
}

export function createDemoRepository(
  options: RepositoryOptions = {},
): DemoData {
  const scenario = options.scenario ?? (seedConfig.scenario as Scenario);
  if (scenario === "error") throw new DemoRepositoryError();
  if (scenario === "empty") {
    return {
      generatedAt: seedConfig.generatedAt,
      scenario,
      instances: [],
      incidents: [],
      deployments: [],
      migrations: [],
    };
  }
  const random = mulberry32(options.seed ?? seedConfig.seed);
  const instances = buildInstances(random, scenario);
  return {
    generatedAt: seedConfig.generatedAt,
    scenario,
    instances,
    incidents: buildIncidents(instances, scenario),
    deployments: buildDeployments(scenario),
    migrations: buildMigrations(),
  };
}

export function filterByEnvironment<T extends { environment: string }>(
  items: T[],
  environment: Environment,
): T[] {
  return environment === "All"
    ? items
    : items.filter((item) => item.environment === environment);
}
